/**
 * Amgen CVOT Study - Self-Contained Module
 *
 * Investigational Medication for Reducing Cardiovascular Risk in Adults with Heart Disease and Excess Weight
 *
 * Target population: Adults 45+ with BMI ≥27 and history of ASCVD (heart attack, stroke, or PAD)
 */

import { defineStudy } from "./registry";
import { amgenCVOTScreeningQuestions } from "../../../data/pre-screener-config/AmgenCVOT";
import { amgCVOTEligibleZipCodes } from "../../../data/cvd-study-zipcodes";

/**
 * Common question IDs that will be asked before study-specific questions
 * and should be filtered out to avoid duplication
 */
const COMMON_QUESTION_IDS = [
  "cardiovascular_event_history", // NEW: Routing question (for ASCVD multi-match pages)
  "ldl_cholesterol_check",
  "high_intensity_statin",
  "major_medical_conditions",
  "recent_medications",
  "height",
  "weight",
  "other_clinical_trials",
  "inPersonAppointments",
];

/**
 * Amgen CVOT Study
 *
 * Priority: 2 (Medium priority in CVD multi-match)
 * Trial ID: VCVDAMG196
 * Location: 2260 zip codes across multiple states
 *
 * Key criteria:
 * - Age ≥45
 * - BMI ≥27
 * - History of ASCVD (heart attack, stroke, or peripheral artery disease)
 * - No Type 1 diabetes
 * - If Type 2 diabetes, HbA1c <11%
 * - No severe heart failure (Class IV)
 * - No GLP-1 use in past 3 months
 */
export const amgenCVOTStudy = defineStudy({
  id: "amgen-cvot",
  name: "Investigational Medication for Reducing Cardiovascular Risk",
  trialId: "VCVDAMG196",
  priority: 2, // Medium priority - for patients with past events (not recent)

  // Specific zip codes (loaded from amg_196.csv - 2260 total)
  eligibleZipCodes: Array.from(amgCVOTEligibleZipCodes),

  // All questions including common ones (will be filtered automatically)
  questions: amgenCVOTScreeningQuestions,

  // Questions to filter out (already asked in common stage)
  commonQuestionIds: COMMON_QUESTION_IDS,

  // ROUTING FILTER: Only consider this study for patients with PAST cardiovascular events
  // (more than 12 months ago). This prevents conflicts with Established cohort (recent events)
  // and High Risk cohort (never had events).
  commonAnswerRequirements: {
    cardiovascular_event_history: ["past_event"],
  },

  metadata: {
    sponsor: "Amgen",
    description:
      "Evaluating whether an investigational medication can help reduce the risk of heart-related events in adults who have heart disease and are also overweight or obese",
  },
});
